import { MonitorMediaFilterType } from '@mnd-frontend/graphql-schema';
import {
  GREY_300,
  Icons,
  RADIUS_LG,
  Text,
  ToggleSwitch,
  media,
  useTranslation,
} from '@mnd-frontend/ui';
import type { ChangeEventHandler } from 'react';
import styled, { css } from 'styled-components';

const SourceCard = styled.div<{ $disabled?: boolean }>`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: row;
  padding: 1rem;
  align-items: center;
  gap: 1rem;
  border-radius: ${RADIUS_LG};
  background-color: ${GREY_300};
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  ${media.lessThan('tablet')} {
    min-height: 9rem;
  }
  &:hover {
    cursor: pointer;
  }

  i,
  svg {
    width: ${({ theme }) => theme.sizingIconSm};
    height: ${({ theme }) => theme.sizingIconSm};
  }
`;

const SourceTextWrapper = styled.div`
  flex: 1;
  user-select: none;
`;

type SourceToggleProps = {
  sourceId: MonitorMediaFilterType;
  name: string;
  checked: boolean;
  disabled?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  toggleSource: (sourceId: MonitorMediaFilterType) => void;
};

type SourceLogoProps = Pick<SourceToggleProps, 'sourceId'>;

const SourceLogo = ({ sourceId }: SourceLogoProps) => {
  switch (sourceId) {
    case MonitorMediaFilterType.Blogs:
      return <Icons.Blog />;
    case MonitorMediaFilterType.Facebook:
      return <Icons.Facebook />;
    case MonitorMediaFilterType.Forums:
      return <Icons.Forum />;
    case MonitorMediaFilterType.Instagram:
      return <Icons.Instagram />;
    case MonitorMediaFilterType.Twitter:
      return <Icons.X />;
    case MonitorMediaFilterType.Linkedin:
      return <Icons.LinkedIn />;
    case MonitorMediaFilterType.Web:
      return <Icons.Story />;
    case MonitorMediaFilterType.Radio:
      return <Icons.Radio />;
    case MonitorMediaFilterType.TvPlay:
      return <Icons.LearningVideo />;
    case MonitorMediaFilterType.TvWeb:
      return <Icons.TvWeb />;
    case MonitorMediaFilterType.Podcast:
      return <Icons.Podcast />;
    case MonitorMediaFilterType.Youtube:
      return <Icons.Youtube />;
    case MonitorMediaFilterType.Print:
      return <Icons.Story />;
    case MonitorMediaFilterType.Tv:
      return <Icons.TV />;
    default:
      return <div></div>;
  }
};

const getSourceExplanation = (t: (key: string) => string, sourceId: MonitorMediaFilterType) => {
  switch (sourceId) {
    case MonitorMediaFilterType.Blogs:
      return t('monitor-reports_sources_blog_description');
    case MonitorMediaFilterType.Facebook:
      return t('monitor-reports_sources_facebook_description');
    case MonitorMediaFilterType.Forums:
      return t('monitor-reports_sources_forums_description');
    case MonitorMediaFilterType.Instagram:
      return t('monitor-reports_sources_instagram_description');
    case MonitorMediaFilterType.Twitter:
      return t('monitor-reports_sources_twitter_description');
    case MonitorMediaFilterType.Web:
      return t('monitor-reports_sources_web_description');
    case MonitorMediaFilterType.Radio:
      return t('monitor_reports_sources_radio_description');
    case MonitorMediaFilterType.TvPlay:
      return t('monitor_reports_sources_tvplay_description');
    case MonitorMediaFilterType.TvWeb:
      return t('monitor_reports_sources_tvweb_description');
    case MonitorMediaFilterType.Podcast:
      return t('monitor_reports_sources_podcast_description');
    case MonitorMediaFilterType.Youtube:
      return t('monitor_reports_sources_youtube_description');
    case MonitorMediaFilterType.Print:
      return t('monitor_reports_sources_print_description');
    case MonitorMediaFilterType.Linkedin:
      return t('monitor-reports_sources_linkedin_description');
    default:
      return '';
  }
};

export const SourceToggle = ({
  sourceId,
  name,
  checked,
  disabled,
  onChange,
  toggleSource,
}: SourceToggleProps) => {
  const { t } = useTranslation();
  return (
    <SourceCard
      $disabled={disabled}
      aria-disabled={disabled}
      onClick={e => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLLabelElement) {
          return;
        }
        toggleSource(sourceId);
      }}
    >
      <SourceLogo sourceId={sourceId} />
      <SourceTextWrapper>
        <Text as="p" variant="labelMedium" textAlign="left">
          {name}
        </Text>
        <Text as="p" variant="bodySmall" textAlign="left" color="GREY_700">
          {getSourceExplanation(t, sourceId)}
        </Text>
      </SourceTextWrapper>
      <ToggleSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </SourceCard>
  );
};
